
/**
 * AES-GCM Encryption Utility
 * Matches Java backend implementation: AESGCMEncryption.java
 * 
 * Algorithm: AES-GCM
 * Key: "helpdesk@nush" (padded to 32 bytes)
 * IV Length: 12 bytes
 * Tag Length: 128 bits
 * Format: IV (12 bytes) + Encrypted Data, Base64 encoded as single string
 */

const SECRET_KEY = "helpdesk@nush"; // Must match Java backend: AESGCMEncryption.java line 19
const CHATBOT_SECRET_KEY =
  process.env.REACT_APP_CHATBOT_API_ENCRYPTION_KEY || "chatbot@transport";

const encoder = new TextEncoder();
const decoder = new TextDecoder();

/**
 * Pad key to 32 bytes (AES-256) - matches Java getSecretKey() method
 * Java: byte[] paddedKeyBytes = new byte[32];
 *       System.arraycopy(keyBytes, 0, paddedKeyBytes, 0, Math.min(keyBytes.length, paddedKeyBytes.length));
 */
function padKey(key) {
  const keyBytes = encoder.encode(key);
  const paddedKeyBytes = new Uint8Array(32); // AES-256 key length (32 bytes)
  paddedKeyBytes.set(keyBytes.slice(0, Math.min(keyBytes.length, 32)), 0);
  return paddedKeyBytes;
}

/**
 * Encrypt data using AES-GCM - matches Java encrypt() method
 * Returns: Base64 string with IV + encrypted data combined (single string)
 * 
 * Java format:
 * - IV: 12 bytes (random)
 * - Encrypted data: includes authentication tag
 * - Combined: IV + encrypted data
 * - Return: Base64.getEncoder().encodeToString(combined)
 */
async function encryptWithKey(data, secretKey) {
  const dataString = typeof data === "string" ? data : JSON.stringify(data);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const key = await crypto.subtle.importKey(
    "raw",
    padKey(secretKey),
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt"]
  );
  const encrypted = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv, tagLength: 128 },
    key,
    encoder.encode(dataString)
  );
  const combined = new Uint8Array(iv.length + encrypted.byteLength);
  combined.set(iv, 0);
  combined.set(new Uint8Array(encrypted), iv.length);
  return btoa(String.fromCharCode(...combined));
}

export async function encryptData(data) {
  try {
    return await encryptWithKey(data, SECRET_KEY);
  } catch (error) {
    console.error("Encryption error:", error);
    throw new Error("Failed to encrypt data: " + error.message);
  }
}

/** AES-GCM for chatbot routes — key: CHATBOT_API_ENCRYPTION_KEY (default chatbot@transport) */
export async function encryptChatbotData(data) {
  try {
    return await encryptWithKey(data, CHATBOT_SECRET_KEY);
  } catch (error) {
    console.error("Chatbot encryption error:", error);
    throw new Error("Failed to encrypt chatbot data: " + error.message);
  }
}

/**
 * Decrypt data using AES-GCM - matches Java decrypt() method
 * 
 * Java format:
 * - Input: Base64 string
 * - Decode: Base64.getDecoder().decode(encryptedData)
 * - Extract IV: first 12 bytes
 * - Extract encrypted data: rest of bytes
 * - Decrypt and return string
 */
function normalizeEncryptedInput(encryptedData) {
  if (typeof encryptedData === "object" && encryptedData !== null) {
    if (encryptedData.iv && encryptedData.data) {
      const iv = Uint8Array.from(atob(encryptedData.iv), (c) => c.charCodeAt(0));
      const data = Uint8Array.from(atob(encryptedData.data), (c) => c.charCodeAt(0));
      const combined = new Uint8Array(iv.length + data.length);
      combined.set(iv, 0);
      combined.set(data, iv.length);
      return btoa(String.fromCharCode(...combined));
    }
    if (encryptedData.encrypted) {
      return encryptedData.encrypted;
    }
    return JSON.stringify(encryptedData);
  }
  return encryptedData;
}

/** AES-GCM wire: 12-byte IV + ciphertext incl. 16-byte tag → min 28 bytes before Base64 */
export function isLikelyAesGcmWire(text) {
  if (typeof text !== "string") {
    return false;
  }
  const trimmed = text.trim();
  if (trimmed.length < 28) {
    return false;
  }
  if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
    return false;
  }
  try {
    const combined = Uint8Array.from(atob(trimmed), (c) => c.charCodeAt(0));
    return combined.length >= 28;
  } catch {
    return false;
  }
}

async function decryptWithKey(encryptedData, secretKey, { parseJson = true } = {}) {
  const normalized = normalizeEncryptedInput(encryptedData);
  if (typeof normalized !== "string") {
    throw new Error("Invalid encrypted data format");
  }

  const trimmed = normalized.trim();
  if (!isLikelyAesGcmWire(trimmed)) {
    throw new Error("The provided data is too small");
  }
  const combined = Uint8Array.from(atob(trimmed), (c) => c.charCodeAt(0));
  const iv = combined.slice(0, 12);
  const encryptedBytes = combined.slice(12);

  const key = await crypto.subtle.importKey(
    "raw",
    padKey(secretKey),
    { name: "AES-GCM", length: 256 },
    false,
    ["decrypt"]
  );

  const decrypted = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv, tagLength: 128 },
    key,
    encryptedBytes
  );

  const decryptedString = decoder.decode(decrypted);
  if (!parseJson) {
    return decryptedString;
  }
  try {
    return JSON.parse(decryptedString);
  } catch {
    return decryptedString;
  }
}

export async function decryptData(encryptedData) {
  try {
    return await decryptWithKey(encryptedData, SECRET_KEY, { parseJson: true });
  } catch (error) {
    console.error("Decryption error:", error);
    throw new Error("Failed to decrypt data: " + error.message);
  }
}

/** AES-GCM decrypt for chatbot responses (HTML or JSON) */
export async function decryptChatbotData(encryptedData, { parseJson = false } = {}) {
  try {
    return await decryptWithKey(encryptedData, CHATBOT_SECRET_KEY, { parseJson });
  } catch (error) {
    console.error("Chatbot decryption error:", error);
    throw new Error("Failed to decrypt chatbot data: " + error.message);
  }
}
